import { ReportAlignment, ReportPageSetup } from './types';

/** Bundled My.Septim logo (blue propeller + wordmark), served same-origin. */
export const DEFAULT_LOGO_SRC = 'images/logo.svg';
export const DEFAULT_LOGO_HEIGHT_MM = 12;

export const DEFAULT_PAGE_SETUP: ReportPageSetup = {
  size: 'A4',
  orientation: 'portrait',
  marginMm: 12,
  border: true,
};

/** Sheet dimensions in mm, portrait. Landscape swaps width and height. */
export const PAGE_SIZES_MM: Record<string, { width: number; height: number }> = {
  A4: { width: 210, height: 297 },
  A5: { width: 148, height: 210 },
  Letter: { width: 215.9, height: 279.4 },
};

/** CSS reference pixels per mm at 96 dpi — how a mm length renders on screen. */
export const PX_PER_MM = 96 / 25.4;

/** Body font of a printed report; also the reference for the em column unit. */
export const PRINT_FONT_SIZE_PT = 9;

/** 1pt = 0.3528mm, so one em of the print font is this many mm. */
export const MM_PER_PT = 0.3528;

/** Numeric column types, per the dataset definition's item.type. */
export const NUMERIC_COLUMN_TYPES = [
  'integer',
  'float',
  'number',
  'numeric',
  'decimal',
  'double',
];

/** Fallback column widths (em) when the definition carries no minWidth. */
export const DEFAULT_COLUMN_WIDTHS: Record<string, number> = {
  float: 12,
  integer: 8,
  number: 12,
  numeric: 12,
  decimal: 12,
  double: 12,
  date: 12,
  time: 10,
  dateTime: 16,
  boolean: 8,
  string: 20,
};

export const FALLBACK_COLUMN_WIDTH = 16;

export const NUMERIC_ALIGNMENT: ReportAlignment = 'right';

/**
 * Column names a VAT breakdown can be derived from. Verified against the 73
 * shipped dataset definitions — not guessed: a breakdown is only generated when
 * a rate column and both an amount pair member are actually present.
 */
export const TAX_RATE_ITEM_NAMES = ['taxRate'];
// Prefer amounts populated only on dedicated VAT rows. Mixed reports such as
// Sales Journal also expose report-wide totals on day/register/payment rows;
// choosing those first would count the same sale repeatedly under a blank rate.
export const TAX_NET_ITEM_NAMES = ['netAmount', 'totalNetAmount', 'totalNet'];
export const TAX_GROSS_ITEM_NAMES = [
  'grossAmount',
  'totalGrossAmount',
  'totalGross',
];

/**
 * Header/footer grid resolution. A dropped element snaps to one of these
 * columns, so any placement the designer allows is one the renderer can print.
 */
export const REPORT_GRID_COLUMNS = 12;

/** Upper bounds exposed by the designer and enforced before rendering. */
export const MAX_PAGE_MARGIN_MM = 40;
export const MAX_COLUMN_WIDTH_EM = 80;
export const MAX_FONT_SIZE_PT = 48;
export const MAX_LOGO_HEIGHT_MM = 60;
export const MAX_SPACER_HEIGHT_MM = 100;

/** Beyond this total column width (em) a generated report switches to landscape. */
export const PORTRAIT_WIDTH_BUDGET_EM = 52;

export const SYSTEM_TEMPLATE_ID_SUFFIX = 'default';
